import { FC, useMemo } from "react";
import { Button } from "@/components/button";
import { ColumnDef } from "@tanstack/react-table";
import { Table } from "@/components/table/table";
import { DataImportSummarySchema } from "@/domains/openapi/types";
import { orderBy } from "lodash-es";
import { DateTime } from "luxon";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { BooleanFieldRow } from "@/components/table/boolean-field-row";
import { NumberFieldRow } from "@/components/table/number-field-row";

interface DataImportsTableProps {
  data: DataImportSummarySchema[];
  onViewDataImport: (id: string) => void;
  pageSize: number;
}

export const DataImportsTable: FC<DataImportsTableProps> = ({
  data,
  onViewDataImport,
  pageSize,
}) => {
  const columns = useMemo<ColumnDef<DataImportSummarySchema>[]>(
    () => [
      {
        header: "ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "Internal Created At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.internal_created_at} />,
      },
      {
        header: "Started At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.started_at} />,
      },
      {
        header: "Ended At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.ended_at} />,
      },
      {
        header: "Ended With Error",
        cell: ({ row }) => <BooleanFieldRow value={row.original.ended_with_error} />,
      },
      {
        header: "Item Count",
        cell: ({ row }) => <NumberFieldRow value={row.original.data_import_item_count} />,
      },
      {
        header: "View",
        cell: ({ row }) => (
          <Button size="1" onClick={() => onViewDataImport(row.original.id)}>
            View
          </Button>
        ),
      },
    ],
    [onViewDataImport]
  );

  const orderedData = useMemo(
    () =>
      orderBy(
        data,
        x => (x.locally_created_at ? DateTime.fromISO(x.locally_created_at).toMillis() : -Infinity),
        "desc"
      ),
    [data]
  );
  return <Table data={orderedData} columns={columns} pageSize={pageSize} />;
};
